import React, { useEffect, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import {
  Search,
  LayoutGrid,
  List,
  Briefcase,
  MapPin,
  ArrowRight,
  ChevronLeft,
  ChevronRight,
  AlertCircle,
  X,
  Loader2,
  Calendar,
} from 'lucide-react';
import {
  getPublicJobsByOrganizationService,
  getPublicJobLinkService,
  getPublicOrganizationByIdService,
} from '../../Services/publicJobs.service';

const DEFAULT_PAGE_SIZE = 12;
const PAGE_SIZE_OPTIONS = [6, 12, 24, 48];
const SEARCH_DEBOUNCE_MS = 400;

function getJobTitle(job) {
  return job?.jobrole || job?.jobRole || job?.job_role || 'Untitled Role';
}

function getExperienceLabel(job) {
  const minExp = job?.description?.minExp ?? job?.minExp;
  const maxExp = job?.description?.maxExp ?? job?.maxExp ?? job?.experienceLevel;

  if (maxExp === undefined || maxExp === null || maxExp === '') return 'Not specified';
  if (typeof maxExp === 'number' && maxExp === 0 && (minExp === 0 || minExp === undefined)) {
    return 'Fresher';
  }
  if (typeof maxExp === 'number') {
    return `Max ${maxExp} ${maxExp === 1 ? 'yr' : 'yrs'} experience`;
  }
  return String(maxExp);
}

function ApplyNowButton({ job, isListView }) {
  const [isApplying, setIsApplying] = useState(false);
  const [applyError, setApplyError] = useState(null);

  const handleApply = async () => {
    setIsApplying(true);
    setApplyError(null);
    try {
      const data = await getPublicJobLinkService(job._id);
      if (data?.link_token) {
        window.open(`/apply/${data.link_token}`, '_blank', 'noopener,noreferrer');
      } else {
        setApplyError("Applications aren't open for this role right now.");
      }
    } catch (err) {
      console.error('Failed to fetch application link for job', job._id, err);
      const status = err?.response?.status;
      if (status === 404) {
        setApplyError("Applications aren't open for this role right now.");
      } else if (status === 400) {
        setApplyError('Something is wrong with this job listing.');
      } else {
        setApplyError('Unable to start application. Please try again.');
      }
    } finally {
      setIsApplying(false);
    }
  };

  return (
    <div className={isListView ? '' : 'flex-1'}>
      <button
        onClick={handleApply}
        disabled={isApplying}
        className={`group/btn flex items-center justify-center gap-2 whitespace-nowrap font-semibold text-sm text-white bg-gradient-to-r from-pink-500 to-blue-500 hover:opacity-90 active:scale-[0.98] transition-all duration-150 rounded-full px-6 py-2.5 shadow-md disabled:opacity-60 disabled:cursor-not-allowed w-full`}
      >
        {isApplying ? (
          <Loader2 className="w-4 h-4 animate-spin" />
        ) : (
          <>
            Apply Now
            <ArrowRight className="w-4 h-4 transition-transform group-hover/btn:translate-x-0.5" />
          </>
        )}
      </button>
      {applyError && (
        <p className="text-xs text-red-500 mt-1.5 text-center">{applyError}</p>
      )}
    </div>
  );
}

function JobCard({ job, isListView, onViewJD }) {
  const title = getJobTitle(job);
  const location = job?.location || 'Location not specified';

  return (
    <div
      className={`group bg-cardPrimary rounded-2xl shadow-sm border border-gray-100 hover:shadow-xl hover:border-transparent hover:-translate-y-0.5 transition-all duration-200 flex ${
        isListView ? 'flex-col sm:flex-row sm:items-center gap-5 p-5' : 'flex-col h-full'
      }`}
    >
      {/* Top: icon + title */}
      <div className={`flex items-start gap-4 ${isListView ? 'flex-1 min-w-0' : 'p-6 pb-4'}`}>
        <div className="flex-shrink-0 w-12 h-12 rounded-xl bg-gradient-to-r from-pink-500 to-blue-500 flex items-center justify-center text-white shadow-sm">
          <Briefcase className="w-5 h-5" />
        </div>

        <div className="min-w-0 flex-1">
          <h3 className="text-lg font-semibold text-textPrimary leading-snug group-hover:text-textHeading transition-colors">
            {title}
          </h3>
        </div>
      </div>

      {!isListView && <div className="border-t border-gray-100 mx-6" />}

      {/* Middle: meta info */}
      <div className={isListView ? 'flex-shrink-0 flex flex-wrap items-center gap-x-6 gap-y-2' : 'px-6 py-4 flex-1 flex flex-col gap-2.5'}>
        <span className="flex items-center gap-2 text-sm text-textSecondary">
          <MapPin className="w-4 h-4 text-gray-400 flex-shrink-0" />
          {location}
        </span>
        <span className="flex items-center gap-2 text-sm text-textSecondary">
          <Briefcase className="w-4 h-4 text-gray-400 flex-shrink-0" />
          {getExperienceLabel(job)}
        </span>
      </div>

      {/* Footer: CTA */}
      <div className={`flex items-center gap-3 ${isListView ? 'flex-shrink-0' : 'px-6 pb-6 pt-1'}`}>
        <button
          onClick={() => onViewJD(job)}
          className={`font-semibold text-sm text-textHeading border border-[#4071FF] hover:bg-[#4071FF]/10 active:scale-[0.98] transition-all duration-150 rounded-full px-5 py-2.5 ${
            isListView ? '' : 'flex-1'
          }`}
        >
          View JD
        </button>
        <ApplyNowButton job={job} isListView={isListView} />
      </div>
    </div>
  );
}

function JobCardSkeleton({ isListView }) {
  return (
    <div
      className={`bg-cardPrimary rounded-2xl border border-gray-100 animate-pulse ${
        isListView ? 'flex items-center gap-5 p-5' : 'p-6 h-56'
      }`}
    >
      <div className="w-12 h-12 rounded-xl bg-gray-200 flex-shrink-0" />
      <div className={isListView ? 'flex-1 space-y-2' : 'mt-4 space-y-2'}>
        <div className="h-4 bg-gray-200 rounded w-2/3" />
        <div className="h-3 bg-gray-100 rounded w-1/2" />
        <div className="h-3 bg-gray-100 rounded w-1/3" />
      </div>
    </div>
  );
}

// Section headers commonly found in free-text job descriptions (with or without a trailing colon).
const SECTION_HEADER_PATTERN =
  /^(about (the )?(company|us|role)|role overview|(key )?responsibilities|roles? (and|&) responsibilities|required skills|good to have|preferred skills|qualifications|requirements|what you'?ll do|nice to have)\s*:?$/i;

function parseJobDescriptionSections(rawText) {
  const lines = (rawText || '')
    .split('\n')
    .map((line) => line.trim())
    .filter(Boolean);

  const sections = [];
  let current = { heading: null, lines: [] };

  lines.forEach((line) => {
    const cleanLine = line.replace(/^[-•*]\s*/, '').trim();
    if (SECTION_HEADER_PATTERN.test(cleanLine.replace(/:$/, ''))) {
      if (current.heading || current.lines.length) sections.push(current);
      current = { heading: cleanLine.replace(/:$/, ''), lines: [] };
    } else {
      current.lines.push({ text: cleanLine, wasBullet: /^[-•*]\s*/.test(line) });
    }
  });
  if (current.heading || current.lines.length) sections.push(current);

  return sections;
}

function JobDetailsModal({ job, onClose }) {
  if (!job) return null;

  const title = getJobTitle(job);
  const location = job?.location || 'Location not specified';
  const description = job?.description || {};
  const rawText = job?.jobDescription || description?.Raw_Job_Description || '';
  const tools = description?.Tools_and_Technologies || [];
  const goodToHave = description?.GoodtoHave || [];
  const rawRequiredSkills = description?.Raw_Required_Skills || [];

  const sections = parseJobDescriptionSections(rawText);
  const hasStructuredSections = sections.some((s) => s.heading);
  const responsibilities = description?.Roles_and_Responsibilities || [];

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center bg-black/40 backdrop-blur-sm p-4"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[85vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-start justify-between gap-4 p-6 pb-4 flex-shrink-0 border-b border-gray-100">
          <div className="flex items-center gap-4 min-w-0">
            <div className="flex-shrink-0 w-12 h-12 rounded-xl bg-gradient-to-r from-pink-500 to-blue-500 flex items-center justify-center text-white shadow-sm">
              <Briefcase className="w-5 h-5" />
            </div>
            <div className="min-w-0">
              <h2 className="text-lg font-semibold text-textPrimary truncate">{title}</h2>
              <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-1 text-sm text-textSecondary">
                <span className="flex items-center gap-1.5">
                  <MapPin className="w-3.5 h-3.5 text-gray-400" />
                  {location}
                </span>
                <span className="flex items-center gap-1.5">
                  <Briefcase className="w-3.5 h-3.5 text-gray-400" />
                  {getExperienceLabel(job)}
                </span>
              </div>
            </div>
          </div>
          <button
            onClick={onClose}
            aria-label="Close"
            className="flex-shrink-0 text-gray-400 hover:text-textPrimary transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Body */}
        <div className="px-6 py-5 overflow-y-auto flex-1 space-y-6">
          {hasStructuredSections ? (
            sections.map((section, idx) => (
              <div key={idx}>
                {section.heading && (
                  <h3 className="text-sm font-semibold text-textPrimary mb-2.5">{section.heading}</h3>
                )}
                {section.lines.length > 0 &&
                  (section.lines.some((l) => l.wasBullet) || section.lines.length > 1 ? (
                    <ul className="space-y-2">
                      {section.lines.map((line, i) => (
                        <li
                          key={i}
                          className="text-sm text-textSecondary leading-relaxed flex items-start gap-2.5"
                        >
                          <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-[#4071FF] flex-shrink-0" />
                          {line.text}
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <p className="text-sm text-textSecondary leading-relaxed">{section.lines[0].text}</p>
                  ))}
              </div>
            ))
          ) : rawText ? (
            <p className="text-sm text-textSecondary leading-relaxed whitespace-pre-wrap">{rawText}</p>
          ) : null}

          {responsibilities.length > 0 && !hasStructuredSections && (
            <div>
              <h3 className="text-sm font-semibold text-textPrimary mb-2.5">Key Responsibilities</h3>
              <ul className="space-y-2">
                {responsibilities.map((item, i) => (
                  <li key={i} className="text-sm text-textSecondary leading-relaxed flex items-start gap-2.5">
                    <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-[#4071FF] flex-shrink-0" />
                    {item}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {rawRequiredSkills.length > 0 && (
            <div>
              <h3 className="text-sm font-semibold text-textPrimary mb-2.5">Required Skills</h3>
              <div className="flex flex-wrap gap-2">
                {rawRequiredSkills.map((skill, i) => (
                  <span
                    key={i}
                    className="text-xs font-medium text-textHeading bg-[#4071FF]/10 px-3 py-1 rounded-full"
                  >
                    {skill}
                  </span>
                ))}
              </div>
            </div>
          )}

          {tools.length > 0 && (
            <div>
              <h3 className="text-sm font-semibold text-textPrimary mb-2">Tools &amp; Technologies</h3>
              <div className="flex flex-wrap gap-2">
                {tools.map((tool, i) => (
                  <span
                    key={i}
                    className="text-xs font-medium text-textSecondary bg-gray-100 px-3 py-1 rounded-full"
                  >
                    {tool}
                  </span>
                ))}
              </div>
            </div>
          )}

          {goodToHave.length > 0 && (
            <div>
              <h3 className="text-sm font-semibold text-textPrimary mb-2">Good to Have</h3>
              <div className="flex flex-wrap gap-2">
                {goodToHave.map((item, i) => (
                  <span
                    key={i}
                    className="text-xs font-medium text-textSecondary bg-gray-100 px-3 py-1 rounded-full"
                  >
                    {item}
                  </span>
                ))}
              </div>
            </div>
          )}

          {!rawText && sections.length === 0 && responsibilities.length === 0 && rawRequiredSkills.length === 0 && (
            <p className="text-sm text-textSecondary">No additional details are available for this role.</p>
          )}
        </div>

        {/* Footer */}
        <div className="p-6 pt-4 flex-shrink-0 border-t border-gray-100">
          <ApplyNowButton job={job} isListView={false} />
        </div>
      </div>
    </div>
  );
}

function CareersPage() {
  const { organizationId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();

  const resolvedOrganizationId = organizationId || searchParams.get('organization_id');
  const currentPage = Math.max(1, parseInt(searchParams.get('page'), 10) || 1);
  const pageSize = PAGE_SIZE_OPTIONS.includes(parseInt(searchParams.get('page_size'), 10))
    ? parseInt(searchParams.get('page_size'), 10)
    : DEFAULT_PAGE_SIZE;

  const [searchInput, setSearchInput] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [postingDateFrom, setPostingDateFrom] = useState('');
  const [isListView, setIsListView] = useState(false);
  const [selectedJob, setSelectedJob] = useState(null);
  const [organizationName, setOrganizationName] = useState('Company');

  const [jobs, setJobs] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, page_size: pageSize, total: 0, total_pages: 1 });
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  // Debounce free-text search before it triggers a server request.
  useEffect(() => {
    const handle = setTimeout(() => setDebouncedSearch(searchInput.trim()), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(handle);
  }, [searchInput]);

  // Reset to page 1 whenever search, date filter, or page size changes.
  useEffect(() => {
    const next = new URLSearchParams(searchParams);
    next.set('page', '1');
    setSearchParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearch, postingDateFrom, pageSize]);

  useEffect(() => {
    if (!resolvedOrganizationId) return;

    let isCancelled = false;
    getPublicOrganizationByIdService(resolvedOrganizationId)
      .then((data) => {
        if (!isCancelled && data?.OrganizationName) {
          setOrganizationName(data.OrganizationName);
        }
      })
      .catch(() => {
        if (!isCancelled) setOrganizationName('Company');
      });

    return () => {
      isCancelled = true;
    };
  }, [resolvedOrganizationId]);

  useEffect(() => {
    if (!resolvedOrganizationId) {
      setIsLoading(false);
      setError('Missing organization reference. This careers link looks invalid.');
      return;
    }

    let isCancelled = false;

    const fetchJobs = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const data = await getPublicJobsByOrganizationService(
          resolvedOrganizationId,
          currentPage,
          pageSize,
          { postingDateFrom, search: debouncedSearch },
        );
        if (isCancelled) return;
        setJobs(Array.isArray(data?.jobs) ? data.jobs : []);
        setPagination({
          page: data?.pagination?.page ?? currentPage,
          page_size: data?.pagination?.page_size ?? pageSize,
          total: data?.pagination?.total ?? 0,
          total_pages: data?.pagination?.total_pages ?? 1,
        });
      } catch (err) {
        if (isCancelled) return;
        const status = err?.response?.status;
        if (status === 422) {
          setError('Invalid request. Please check the careers link and try again.');
        } else {
          setError(err?.response?.data?.detail || 'Something went wrong while loading jobs. Please try again.');
        }
        setJobs([]);
      } finally {
        if (!isCancelled) setIsLoading(false);
      }
    };

    fetchJobs();
    return () => {
      isCancelled = true;
    };
  }, [resolvedOrganizationId, currentPage, pageSize, postingDateFrom, debouncedSearch]);

  const goToPage = (page) => {
    if (page < 1 || page > pagination.total_pages || page === currentPage) return;
    const next = new URLSearchParams(searchParams);
    next.set('page', String(page));
    setSearchParams(next);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handlePageSizeChange = (size) => {
    const next = new URLSearchParams(searchParams);
    next.set('page_size', String(size));
    setSearchParams(next);
  };

  return (
    <div className="min-h-screen bg-bgSecondary">
      {/* Header */}
      <header className="sticky top-0 z-20 bg-bgPrimary/90 backdrop-blur border-b border-gray-100 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex flex-col md:flex-row md:items-center gap-4 md:gap-6">
            {/* Organization name - top left */}
            <div className="flex items-center gap-3 flex-shrink-0">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-r from-pink-500 to-blue-500 flex items-center justify-center text-white font-bold text-lg">
                {organizationName.charAt(0)}
              </div>
              <span className="text-xl font-bold text-textPrimary whitespace-nowrap">
                {organizationName}
              </span>
            </div>

            {/* Search bar - center */}
            <div className="flex-1 w-full">
              <div className="relative max-w-xl mx-auto md:mx-0">
                <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4.5 h-4.5 text-gray-400" />
                <input
                  type="text"
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                  placeholder="Search jobs by title..."
                  className="w-full pl-10 pr-4 py-2.5 rounded-full border border-gray-200 bg-bgSecondary text-sm text-textPrimary placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#4071FF]/40 focus:border-[#4071FF] transition-all"
                />
              </div>
            </div>

            {/* Layout toggle - right */}
            <div className="flex items-center gap-2 flex-shrink-0 justify-end">
              <div className="flex items-center border border-gray-200 rounded-full p-1 bg-bgSecondary">
                <button
                  onClick={() => setIsListView(false)}
                  aria-label="Grid view"
                  className={`p-2 rounded-full transition-colors ${
                    !isListView ? 'bg-white shadow text-textHeading' : 'text-gray-400 hover:text-textSecondary'
                  }`}
                >
                  <LayoutGrid className="w-4 h-4" />
                </button>
                <button
                  onClick={() => setIsListView(true)}
                  aria-label="List view"
                  className={`p-2 rounded-full transition-colors ${
                    isListView ? 'bg-white shadow text-textHeading' : 'text-gray-400 hover:text-textSecondary'
                  }`}
                >
                  <List className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>

          {/* Secondary filter row */}
          <div className="flex flex-wrap items-center gap-3 mt-3 pt-3 border-t border-gray-100">
            <div className="flex items-center gap-2">
              <Calendar className="w-4 h-4 text-gray-400" />
              <label htmlFor="posting-date-from" className="text-xs font-medium text-textSecondary whitespace-nowrap">
                Posted after
              </label>
              <input
                id="posting-date-from"
                type="date"
                value={postingDateFrom}
                onChange={(e) => setPostingDateFrom(e.target.value)}
                className="text-sm text-textPrimary border border-gray-200 rounded-full px-3 py-1.5 bg-bgSecondary focus:outline-none focus:ring-2 focus:ring-[#4071FF]/40 focus:border-[#4071FF] transition-all"
              />
              {postingDateFrom && (
                <button
                  onClick={() => setPostingDateFrom('')}
                  aria-label="Clear date filter"
                  className="text-gray-400 hover:text-textSecondary transition-colors"
                >
                  <X className="w-3.5 h-3.5" />
                </button>
              )}
            </div>

            <div className="flex items-center gap-2 ml-auto">
              <label htmlFor="page-size" className="text-xs font-medium text-textSecondary whitespace-nowrap">
                Items per page
              </label>
              <select
                id="page-size"
                value={pageSize}
                onChange={(e) => handlePageSizeChange(Number(e.target.value))}
                className="text-sm text-textPrimary border border-gray-200 rounded-full pl-3 pr-8 py-1.5 bg-bgSecondary focus:outline-none focus:ring-2 focus:ring-[#4071FF]/40 focus:border-[#4071FF] transition-all cursor-pointer"
              >
                {PAGE_SIZE_OPTIONS.map((size) => (
                  <option key={size} value={size}>
                    {size}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>
      </header>

      {/* Main content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-6">
          <h1 className="text-2xl sm:text-3xl font-bold text-textPrimary">
            Join Our Team
          </h1>
          <p className="text-sm text-textSecondary mt-1">
            {isLoading
              ? 'Loading open positions…'
              : `${pagination.total} ${pagination.total === 1 ? 'job' : 'jobs'} available at ${organizationName}`}
          </p>
        </div>

        {error ? (
          <div className="flex flex-col items-center justify-center text-center py-24 bg-cardPrimary rounded-2xl border border-gray-100">
            <AlertCircle className="w-12 h-12 text-red-400 mb-4" />
            <h3 className="text-lg font-semibold text-textPrimary">Unable to load jobs</h3>
            <p className="text-sm text-textSecondary mt-1 max-w-md">{error}</p>
          </div>
        ) : isLoading ? (
          <div
            className={
              isListView
                ? 'flex flex-col gap-4'
                : 'grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-6'
            }
          >
            {Array.from({ length: pageSize }, (_, i) => (
              <JobCardSkeleton key={i} isListView={isListView} />
            ))}
          </div>
        ) : jobs.length === 0 ? (
          <div className="flex flex-col items-center justify-center text-center py-24 bg-cardPrimary rounded-2xl border border-gray-100">
            <Briefcase className="w-12 h-12 text-gray-300 mb-4" />
            <h3 className="text-lg font-semibold text-textPrimary">No jobs found</h3>
            <p className="text-sm text-textSecondary mt-1">
              {debouncedSearch || postingDateFrom
                ? 'Try adjusting your search or date filter.'
                : 'There are no open positions right now. Please check back later.'}
            </p>
          </div>
        ) : (
          <div
            className={
              isListView
                ? 'flex flex-col gap-4'
                : 'grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-6'
            }
          >
            {jobs.map((job) => (
              <JobCard
                key={job._id}
                job={job}
                isListView={isListView}
                onViewJD={setSelectedJob}
              />
            ))}
          </div>
        )}

        {/* Pagination - server driven */}
        {!error && !isLoading && pagination.total_pages > 1 && (
          <div className="flex items-center justify-center gap-2 mt-10">
            <button
              onClick={() => goToPage(currentPage - 1)}
              disabled={currentPage === 1}
              className="p-2 rounded-full border border-gray-200 text-textSecondary disabled:opacity-40 disabled:cursor-not-allowed hover:bg-gray-50 transition-colors"
              aria-label="Previous page"
            >
              <ChevronLeft className="w-4 h-4" />
            </button>

            {Array.from({ length: pagination.total_pages }, (_, i) => i + 1).map((page) => (
              <button
                key={page}
                onClick={() => goToPage(page)}
                className={`w-9 h-9 rounded-full text-sm font-medium transition-colors ${
                  page === currentPage
                    ? 'bg-[#4071FF] text-white'
                    : 'text-textSecondary hover:bg-gray-100'
                }`}
              >
                {page}
              </button>
            ))}

            <button
              onClick={() => goToPage(currentPage + 1)}
              disabled={currentPage === pagination.total_pages}
              className="p-2 rounded-full border border-gray-200 text-textSecondary disabled:opacity-40 disabled:cursor-not-allowed hover:bg-gray-50 transition-colors"
              aria-label="Next page"
            >
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        )}
      </main>

      <JobDetailsModal job={selectedJob} onClose={() => setSelectedJob(null)} />
    </div>
  );
}

export default CareersPage;
